import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { selectSessionAdvisor } from '@client/model';
import { inviteApi } from '@client/features/invite-client';
import { sendReminderApi } from '../api/api';

export const DEFAULT_REMINDER_EMAIL_BODY = `I wanted to follow up on the long-term care planning questionnaire I sent earlier. Completing this brief form (about 5 minutes) will allow me to generate personalized projections for your future care needs.

Once you've completed it, I'll be able to show you:
• Your projected long-term care costs
• A personalized care plan based on your preferences
• Different funding options we could explore together

Please let me know if you have any questions or need help accessing the form.`;

export interface SendReminderConfig {
  email: string;
  firstName: string;
  lastName: string;
  emailBody: string;
  clientId: string;
  intakeFormUrl: string;
  surveyId: string;
}

export interface SendReminderStoreType {
  reminderConfig: SendReminderConfig;
  isOpen: boolean;
  isLoading: boolean;
  isEmailValid: boolean;
  canSend: boolean;

  setReminderConfig: Dispatch<SetStateAction<SendReminderConfig>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;

  handleConfigChange: (field: keyof SendReminderConfig, value: string) => void;
  handleSendReminder: () => void;
  handleClose: () => void;
  openWithClient: (client: Client) => void;
}

export const SendReminderContext = createContext<SendReminderStoreType | null>(null);

export function useSendReminderStore() {
  const context = useContext(SendReminderContext);

  if (!context) {
    throw new Error('`useSendReminderStore` should be used inside the `<SendReminderProvider />`');
  }

  return context;
}

const isValidEmailFormat = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export function canSendReminder({
  email,
  intakeFormUrl,
}: {
  email?: string | null;
  intakeFormUrl?: string | null;
}): boolean {
  const e = email ?? '';
  const url = intakeFormUrl ?? '';
  return isValidEmailFormat(e) && e.length > 0 && url.length > 0;
}

const initialConfig: SendReminderConfig = {
  email: '',
  firstName: '',
  lastName: '',
  emailBody: DEFAULT_REMINDER_EMAIL_BODY,
  clientId: '',
  intakeFormUrl: '',
  surveyId: '',
};

export function SendReminderProvider({ children }: { children: ReactNode }) {
  const [reminderConfig, setReminderConfig] = useState<SendReminderConfig>(initialConfig);
  const [isOpen, setIsOpen] = useState(false);

  const advisor = useSelector(selectSessionAdvisor);

  const isEmailValid = isValidEmailFormat(reminderConfig.email);
  const canSend = canSendReminder(reminderConfig);
  const handleConfigChange = useCallback((field: keyof SendReminderConfig, value: string) => {
    setReminderConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setReminderConfig(initialConfig);
  }, []);

  const persistUrlMutation = useMutation({
    mutationFn: ({
      clientId,
      intakeFormUrl,
      clientEmail,
    }: {
      clientId: string;
      intakeFormUrl: string;
      clientEmail: string;
    }) => sendReminderApi.persistIntakeFormUrl(clientId, intakeFormUrl, clientEmail),
  });

  const intakeAccessMutation = useMutation({
    mutationFn: ({
      clientId,
      email,
      firstName,
      lastName,
    }: {
      clientId: string;
      email: string;
      firstName: string;
      lastName: string;
    }) => sendReminderApi.provisionIntakeAccess(clientId, { email, firstName, lastName }),
  });

  const reminderMutation = useMutation({
    mutationFn: inviteApi.sendInvite,
    onSuccess: (data, variables) => {
      if (!data.success) {
        toast.error('Failed to send reminder', {
          description: data.error || 'Please try again',
        });
        return;
      }
      if (variables.clientId && variables.intakeFormLink) {
        persistUrlMutation.mutate({
          clientId: variables.clientId,
          intakeFormUrl: variables.intakeFormLink,
          clientEmail: variables.email ?? '',
        });
      }
      toast.success('Reminder sent successfully');
      handleClose();
    },
    onError: error => {
      const message =
        error instanceof Error ? error.message : 'Please try again or contact support';
      toast.error('Failed to send reminder', {
        description: message,
      });
    },
  });

  const handleSendReminder = useCallback(async () => {
    if (!canSend || !advisor) return;

    const email = reminderConfig.email.trim();
    const firstName = reminderConfig.firstName.trim();
    const lastName = reminderConfig.lastName.trim();

    // Provision access BEFORE sending so the emailed sign-in link resolves for clients
    // created without an email/account (e.g. SelfFill). Best-effort: a failure here must
    // not block the reminder itself.
    if (reminderConfig.clientId) {
      try {
        await intakeAccessMutation.mutateAsync({
          clientId: reminderConfig.clientId,
          email,
          firstName,
          lastName,
        });
      } catch {
        // Non-fatal — still send the reminder.
      }
    }

    reminderMutation.mutate({
      clientId: reminderConfig.clientId,
      advisorId: advisor.advisorId,
      email,
      clientFirstName: firstName,
      clientLastName: lastName,
      advisorProvidedBody: reminderConfig.emailBody,
      intakeFormLink: reminderConfig.intakeFormUrl,
      surveyId: reminderConfig.surveyId,
      createNewClient: false,
    });
  }, [canSend, advisor, reminderConfig, reminderMutation, intakeAccessMutation]);

  const openWithClient = useCallback(async (client: Client) => {
    setReminderConfig({
      email: client.clientEmail || '',
      firstName: client.clientFirstName || '',
      lastName: client.clientLastName || '',
      emailBody: DEFAULT_REMINDER_EMAIL_BODY,
      clientId: client.clientId,
      intakeFormUrl: client.intakeFormUrl || '',
      surveyId: client.surveyId || '',
    });
    setIsOpen(true);

    // SelfFill (and other) clients created without an intakeFormUrl still have a
    // form-provider reference. Pull its formLink so the reminder can be sent.
    if (!client.intakeFormUrl && client.clientId) {
      try {
        const { formLink } = await sendReminderApi.fetchFormReference(client.clientId);
        if (formLink) {
          setReminderConfig(prev => ({ ...prev, intakeFormUrl: formLink }));
        }
      } catch {
        // No form reference available; the Send button stays disabled.
      }
    }
  }, []);

  const isLoading = reminderMutation.isPending || intakeAccessMutation.isPending;

  const value: SendReminderStoreType = {
    reminderConfig,
    isOpen,
    isLoading,
    isEmailValid,
    canSend,
    setReminderConfig,
    setIsOpen,
    handleConfigChange,
    handleSendReminder,
    handleClose,
    openWithClient,
  };

  return <SendReminderContext.Provider value={value}>{children}</SendReminderContext.Provider>;
}
